import { z } from 'zod';
import {
  CONTACT_NOTE_MAX, DESCRIPTION_MAX, INQUIRY_STATUSES, ITEM_CONDITIONS, ITEM_STATUSES, LOCATION_MAX, MAX_IMAGES_PER_ITEM,
  MAX_PRICE_CENTS, MAX_SALE_MEMBERS, MESSAGE_MAX, NAME_MAX, PASSWORD_MIN, PRICING_MODES, PRIVATE_ADDRESS_MAX,
  PRIVACY_VERSION, RESERVED_SLUGS, SALE_DESCRIPTION_MAX, SALE_ROLES, SALE_STATUSES, SELLER_REQUEST_STATUSES, TERMS_VERSION, TITLE_MAX, USER_STATUSES,
} from './constants';

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().max(254).pipe(z.email()),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/, 'Use 1-40 lowercase letters, digits or dashes')
  .refine((s) => !(RESERVED_SLUGS as readonly string[]).includes(s), 'That name is reserved');

const priceCents = z.number().int().min(0).max(MAX_PRICE_CENTS);

export const itemCreateSchema = z
  .object({
    // Optional: an untitled item shows as "Item #12" (see itemTitle).
    title: z.string().trim().max(TITLE_MAX).default(''),
    description: z.string().trim().max(DESCRIPTION_MAX).default(''),
    condition: z.enum(ITEM_CONDITIONS).nullable().default(null),
    pricingMode: z.enum(PRICING_MODES),
    priceCents: priceCents.nullable().optional(),
    imageIds: z.array(z.uuid()).min(1, 'Add at least one photo').max(MAX_IMAGES_PER_ITEM),
  })
  .superRefine((v, ctx) => {
    if (v.pricingMode === 'FIXED' && (v.priceCents === null || v.priceCents === undefined)) {
      ctx.addIssue({ code: 'custom', path: ['priceCents'], message: 'Enter a price' });
    }
    if (v.pricingMode === 'FREE' && v.priceCents !== null && v.priceCents !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['priceCents'], message: 'Free items have no price' });
    }
    if (new Set(v.imageIds).size !== v.imageIds.length) {
      ctx.addIssue({ code: 'custom', path: ['imageIds'], message: 'Duplicate photo' });
    }
  });
export type ItemCreateInput = z.infer<typeof itemCreateSchema>;

/**
 * Partial update of an item. The server merges it with the stored item and then applies the
 * same pricing rules as creation (see checkPricing). `imageIds` is the complete, ordered photo list.
 */
export const itemUpdateSchema = z
  .object({
    title: z.string().trim().max(TITLE_MAX),
    description: z.string().trim().max(DESCRIPTION_MAX),
    condition: z.enum(ITEM_CONDITIONS).nullable(),
    pricingMode: z.enum(PRICING_MODES),
    priceCents: priceCents.nullable(),
    status: z.enum(ITEM_STATUSES),
    imageIds: z.array(z.uuid()).min(1, 'Keep at least one photo').max(MAX_IMAGES_PER_ITEM),
  })
  .partial()
  .refine((v) => !v.imageIds || new Set(v.imageIds).size === v.imageIds.length, {
    path: ['imageIds'],
    message: 'Duplicate photo',
  });
export type ItemUpdateInput = z.infer<typeof itemUpdateSchema>;

/** Returns a field error if the price doesn't fit the pricing mode, else null. */
export function checkPricing(mode: (typeof PRICING_MODES)[number], cents: number | null | undefined) {
  if (mode === 'FIXED' && (cents === null || cents === undefined)) return { priceCents: 'Enter a price' };
  if (mode === 'FREE' && cents !== null && cents !== undefined) return { priceCents: 'Free items have no price' };
  return null;
}

const optionalText = (max: number) =>
  z.string().trim().max(max).optional().transform((v) => v || undefined);

export const inquiryCreateSchema = z.object({
  name: z.string().trim().min(1, 'Add your name').max(NAME_MAX),
  phone: z
    .string()
    .trim()
    .max(32)
    .regex(/^[0-9+()\-.\s]+$/, 'Enter a phone number')
    .refine((p) => p.replace(/\D/g, '').length >= 7, 'Enter a phone number'),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .optional()
    .transform((v) => v || undefined)
    .pipe(z.email('Enter a valid email or leave it blank').optional()),
  offerCents: priceCents.nullable().optional(),
  message: optionalText(MESSAGE_MAX),
  /** Honeypot: hidden from people, filled in by bots. Must be empty. */
  website: z.string().max(200).optional(),
});
export type InquiryCreateInput = z.input<typeof inquiryCreateSchema>;

export const inquiryUpdateSchema = z.object({ status: z.enum(INQUIRY_STATUSES) });

export const saleUpdateSchema = z
  .object({
    title: z.string().trim().min(1, 'Add a title').max(TITLE_MAX),
    description: z.string().trim().max(SALE_DESCRIPTION_MAX),
    publicLocation: z.string().trim().min(1, 'Add a general area').max(LOCATION_MAX),
    contactNote: z.string().trim().max(CONTACT_NOTE_MAX).nullable().transform((v) => v || null),
    /** Last day of the sale (YYYY-MM-DD, in the sale's timezone), or null for no deadline. */
    endsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-11-07').nullable(),
    status: z.enum(['ACTIVE', 'CLOSED']),
  })
  .partial();
export type SaleUpdateInput = z.input<typeof saleUpdateSchema>;

// ---------- Platform admin ----------

const emailSchema = z.string().trim().toLowerCase().max(254).pipe(z.email('Enter a valid email'));
const displayNameSchema = z.string().trim().min(1, 'Add a name').max(NAME_MAX);

export const sellerCreateSchema = z.object({
  displayName: displayNameSchema,
  email: emailSchema,
  /** Also email the setup link (only when SMTP is configured). */
  sendEmail: z.boolean().default(false),
  /** The Start Selling request this seller was created from, if any. */
  requestId: z.uuid().optional(),
});
export type SellerCreateInput = z.input<typeof sellerCreateSchema>;

export const sellerUpdateSchema = z
  .object({ displayName: displayNameSchema, email: emailSchema, status: z.enum(USER_STATUSES) })
  .partial();
export type SellerUpdateInput = z.input<typeof sellerUpdateSchema>;

export const setupLinkRequestSchema = z.object({ sendEmail: z.boolean().default(false) });

export const saleMembersSchema = z
  .array(z.object({ userId: z.uuid(), role: z.enum(SALE_ROLES) }))
  .max(MAX_SALE_MEMBERS)
  .refine((m) => new Set(m.map((x) => x.userId)).size === m.length, 'Each seller can be added once');
export type SaleMemberInput = z.infer<typeof saleMembersSchema>[number];

const saleFields = {
  title: z.string().trim().min(1, 'Add a title').max(TITLE_MAX),
  slug: slugSchema,
  description: z.string().trim().max(SALE_DESCRIPTION_MAX),
  publicLocation: z.string().trim().min(1, 'Add a general area').max(LOCATION_MAX),
  privateAddress: z.string().trim().max(PRIVATE_ADDRESS_MAX).nullable().transform((v) => v || null),
  contactNote: z.string().trim().max(CONTACT_NOTE_MAX).nullable().transform((v) => v || null),
  endsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-11-07').nullable(),
  timezone: z.string().trim().min(1).max(64),
  status: z.enum(SALE_STATUSES),
};

export const saleCreateSchema = z.object({
  ...saleFields,
  description: saleFields.description.default(''),
  privateAddress: saleFields.privateAddress.default(null),
  contactNote: saleFields.contactNote.default(null),
  endsOn: saleFields.endsOn.default(null),
  timezone: saleFields.timezone.default('America/Chicago'),
  status: saleFields.status.default('DRAFT'),
  members: saleMembersSchema.default([]),
});
export type SaleCreateInput = z.input<typeof saleCreateSchema>;

export const platformSaleUpdateSchema = z.object(saleFields).partial();
export type PlatformSaleUpdateInput = z.input<typeof platformSaleUpdateSchema>;

export const setupTokenSchema = z.object({ token: z.string().min(20).max(100) });
export const setPasswordSchema = setupTokenSchema.extend({
  password: z.string().min(PASSWORD_MIN, `Use at least ${PASSWORD_MIN} characters`).max(200),
});
export type SetPasswordInput = z.input<typeof setPasswordSchema>;

// ---------- Start Selling requests ----------

/** The public "Start Selling" form. Not an account: an admin reviews it. */
export const sellerRequestSchema = z.object({
  name: z.string().trim().min(1, 'Add your name').max(NAME_MAX),
  email: emailSchema,
  saleName: z.string().trim().max(TITLE_MAX).optional().transform((v) => v || undefined),
  /** Honeypot: hidden from people, filled in by bots. */
  website: z.string().max(200).optional(),
});
export type SellerRequestInput = z.input<typeof sellerRequestSchema>;

export const sellerRequestUpdateSchema = z.object({ status: z.enum(SELLER_REQUEST_STATUSES) });

/** Accepting the Terms and Privacy Policy. The versions must be the current ones the user was shown. */
export const acceptAgreementSchema = z.object({
  accepted: z.literal(true, 'Tick the box to agree'),
  termsVersion: z.literal(TERMS_VERSION, 'The Terms were updated. Reload the page.'),
  privacyVersion: z.literal(PRIVACY_VERSION, 'The Privacy Policy was updated. Reload the page.'),
});
export type AcceptAgreementInput = z.input<typeof acceptAgreementSchema>;
